import {
  CalendarOutlined,
  CloseOutlined,
  PlusOutlined,
  RetweetOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  DatePicker,
  Flex,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Tour,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import useRevenue from "../../utils/useRevenue";

const { Text } = Typography;
const { TextArea } = Input;

const cardOptions = [
  {
    value: "new_event",
    label: "Registrar um novo evento",
    icon: <PlusOutlined />,
  },
  {
    value: "edit_zone_collection",
    label: "Gerenciar zonas e coleções",
    icon: <SettingOutlined />,
  },
];

export default function CollectionsManager() {
  const [messageApi, contextHolder] = message.useMessage();

  const [selectedOption, setSelectedOption] = useState("");

  // Adicionar uma nova coleção
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);

  // Registrar um novo evento
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventComment, setNewEventComment] = useState("");
  const [zoneAffected, setZoneAffected] = useState([]);

  // Adicionar uma Coleção a uma zona
  const [zoneCollection, setZoneCollection] = useState([{}]);
  const [zonesList, setZonesList] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [isAddingCollection, setIsAddingCollection] = useState(false);
  const [updatedZoneCollectionFlag, setUpdateZoneCollectionFlag] =
    useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [newCollectionStartDate, setNewCollectionStartDate] = useState(null);

  // Substituir a coleção de uma zona
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [collectionToReplace, setCollectionToReplace] = useState(null);
  const [selectedNewCollectionForReplace, setSelectedNewCollectionForReplace] =
    useState(null);

  // Remover a coleção de uma zona
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [collectionToRemove, setCollectionToRemove] = useState(null);
  const [selectedRemoveEndDate, setSelectedRemoveEndDate] = useState(null);

  const [datePickerNow, setDatePickerNow] = useState(
    dayjs(new Date().toISOString())
  );
  const [selectedDate, setSelectedDate] = useState("");

  // Editar a data de início de uma coleção
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [collectionToEditStartDate, setCollectionToEditStartDate] =
    useState(null);
  const [selectedEditStartDate, setSelectedEditStartDate] = useState(null);

  const {
    postEvent,
    postCollection,
    getZoneActualCollection,
    patchZoneCollectionUnion,
    postZonesCollections,
    postCollectionChange,
    getZones,
    getCollections,
    getZoneConfigs,
  } = useApi();

  const { getRevenueCollections } = useRevenue();

  const { shouldShowTutorialBool, postShouldNeverShowTutorial } = useAuth();
  const [isSeventhPhaseTutorialOpen, setIsSeventhPhaseTutorialOpen] =
    useState(true);
  const [isEighthPhaseTutorialOpen, setIsEighthPhaseTutorialOpen] =
    useState(false);
  const [isNinthPhaseTutorialOpen, setIsNinthPhaseTutorialOpen] =
    useState(false);
  const [isFinalTutorialModalOpen, setIsFinalTutorialModalOpen] =
    useState(false);

  const navigate = useNavigate();

  const initialLoadRef = useRef(true);

  useEffect(() => {
    const loadCollections = async () => {
      if (!initialLoadRef.current) return;

      try {
        const response = await getRevenueCollections();
        const options = response.data.collections.map((collection) => ({
          value: collection.col_sequencial,
          label: collection.col_descricao,
        }));
        setCollections(options);
        initialLoadRef.current = false;
      } catch (error) {
        console.error("Erro ao carregar coleções:", error);
      }
    };

    loadCollections();
  }, [getRevenueCollections]);

  const feedbackMessage = (type, text) => {
    messageApi.open({
      type: type,
      content: text,
    });
  };

  // Registrar um novo evento
  const createEvent = () => {
    if (zoneAffected === "" || newEventTitle === "" || newEventComment === "") {
      feedbackMessage("error", "Preencha todos os campos!");
      return;
    }

    let params = {
      zone: zoneAffected,
      datetime: selectedDate ? selectedDate : new Date().toISOString(),
      title: newEventTitle,
      comment: newEventComment,
    };

    postEvent(params)
      .then(() => {
        feedbackMessage("success", "Evento registrado com sucesso!");
        setZoneAffected([]);
        setNewEventTitle("");
        setNewEventComment("");
      })
      .catch((error) => {
        feedbackMessage("error", `Algo deu errado!: ${error}`);
      });
  };

  // Obter dados da zona selecionada

  function getZoneCollection(zone) {
    getZoneActualCollection(zone)
      .then((response) => {
        setZoneCollection(response.data);
      })
      .catch((error) => {
        if (error.response.status === 404) {
          feedbackMessage("warning", "Zona não possui coleções cadastradas!");
          setZoneCollection([]);
          return;
        }
        feedbackMessage("error", "Algo deu errado!");
      });
  }

  // Funções auxiliares para gerenciar Zonas e Coleções

  const performRemoveCollection = async (zoneId, legacyId, endTime) => {
    const params2 = {
      union_end_datetime: endTime,
      collection: legacyId,
    };
    await patchZoneCollectionUnion(zoneId, params2);
  };

  const performRegisterCollection = async (
    zoneId,
    legacyId,
    description,
    startDateTime
  ) => {
    const params = { description, legacy_id: legacyId };
    await postCollection(params);

    const zonesParams = {
      zone: zoneId,
      collection: legacyId,
      union_start_datetime: startDateTime || new Date().toISOString(),
    };
    const created = await postZonesCollections(zonesParams);

    const createdId =
      created?.data?.zonecollection_id ??
      created?.data?.zonescollections_id ??
      created?.data?.id ??
      null;

    if (createdId) return createdId;

    const resp = await getZoneActualCollection(zoneId);
    const active = (resp?.data || []).find(
      (col) => col.union_end_datetime === null && col.legacy_id === legacyId
    );
    return active?.zonecollection_id ?? null;
  };

  // Remover uma coleção

  const removeCollection = async (collection, endDate) => {
    const zoneName =
      zonesList.find((zone) => zone.value === zoneId)?.label || zoneId;

    const unionEndTime = endDate
      ? endDate.toISOString()
      : new Date().toISOString();

    try {
      await performRemoveCollection(zoneId, collection.legacy_id, unionEndTime);
      setUpdateZoneCollectionFlag((prev) => !prev);
      feedbackMessage(
        "success",
        `Coleção ${collectionToRemove.collection} removida da zona ${zoneName} com sucesso!`
      );
    } catch (error) {
      feedbackMessage("error", "Algo deu errado ao remover a coleção!");
      console.error(error);
    }
  };

  // Registar o vínculo entre uma zona e coleção e criar a coleção na DataBase da aplicação

  const registerCollectionAndZone = async () => {
    const zoneName =
      zonesList.find((zone) => zone.value === zoneId)?.label || zoneId;

    try {
      const newZonesCollectionsId = await performRegisterCollection(
        zoneId,
        selectedCollection.value,
        selectedCollection.label,
        newCollectionStartDate.toISOString()
      );
      feedbackMessage(
        "success",
        `Nova coleção adicionada e associada à zona ${zoneName} com sucesso. Evento registrado!`
      );

      const currentActiveCollection = zoneCollection.find(
        (col) => col.union_end_datetime === null
      );
      const oldZoneCollectionId = currentActiveCollection
        ? currentActiveCollection.zonecollection_id
        : null;

      const collectionChangeParams = {
        zone: zoneId,
        datetime: newCollectionStartDate.toISOString().slice(0, 10),
        old_zonescollections_id: oldZoneCollectionId,
        new_zonescollections_id: newZonesCollectionsId,
      };

      await postCollectionChange(collectionChangeParams);

      setUpdateZoneCollectionFlag((prev) => !prev);
      setSelectedCollection(null);
      setNewCollectionStartDate(null);
    } catch (error) {
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data.msg ===
          "This zone already has an active relation with the given collection."
      ) {
        feedbackMessage("warning", "Coleção já vinculada a esta zona!");
      } else {
        feedbackMessage("error", "Algo deu errado!");
      }
    }
    setIsFinalTutorialModalOpen(true);
  };

  // Substituir a coleção de uma zona.
  // -> Encerra o relacionamento atual e cria um novo

  const updateCollection = async (oldCollection, newCollectionObj) => {
    const zoneName =
      zonesList.find((zone) => zone.value === zoneId)?.label || zoneId;

    const unionEndTime = selectedEndDate
      ? selectedEndDate.toISOString()
      : new Date().toISOString();

    try {
      // 1) Encerrar relação antiga
      await performRemoveCollection(
        zoneId,
        oldCollection.legacy_id,
        unionEndTime
      );

      // 2) Registrar nova coleção
      const newZonesCollectionsId = await performRegisterCollection(
        zoneId,
        newCollectionObj.value,
        newCollectionObj.label
      );

      // 3) Registrar Collections Change
      const collectionChangeParams = {
        zone: zoneId,
        datetime: unionEndTime.slice(0, 10),
        old_zonescollections_id: oldCollection.zonecollection_id,
        new_zonescollections_id: newZonesCollectionsId,
      };
      await postCollectionChange(collectionChangeParams);

      feedbackMessage(
        "success",
        `Relação substituída com sucesso! ${zoneName} agora possui a nova coleção (${newCollectionObj.label}).`
      );
      setUpdateZoneCollectionFlag((prev) => !prev);
      setShowReplaceModal(false);
      setSelectedNewCollectionForReplace(null);
      setSelectedEndDate(null);
    } catch (error) {
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data.msg ===
          "This zone already has an active relation with the given collection."
      ) {
        feedbackMessage("warning", "Coleção já vinculada a esta zona!");
      } else {
        feedbackMessage("error", "Algo deu errado!");
      }
    }
  };

  // Editar a data de início de uma relação

  const updateStartDate = async () => {
    const unionStartTime = selectedEditStartDate
      ? selectedEditStartDate.toISOString()
      : new Date().toISOString();

    patchZoneCollectionUnion(zoneId, {
      union_start_datetime: unionStartTime,
      collection: collectionToEditStartDate.legacy_id,
    })
      .then(() => {
        feedbackMessage("success", "Data de início atualizada com sucesso!");
        setShowStartDateModal(false);
        setSelectedEditStartDate(null);
        setUpdateZoneCollectionFlag((prev) => !prev);
      })
      .catch((error) => {
        feedbackMessage(
          "error",
          "Algo deu errado ao atualizar a data de início!"
        );
        console.error(error);
      });
  };

  // useEffect para atualizar o card

  useEffect(() => {
    if (zoneId) {
      getZoneCollection(zoneId);
    }
  }, [updatedZoneCollectionFlag, zoneId]);

  // Limpar os estados utilizados
  const cleanUsesState = () => {
    setZoneAffected([]);
    setNewEventTitle("");
    setNewEventComment("");

    setZoneCollection([]);
    setZoneId("");
  };

  const handleFinalTutorial = () => {
    setIsFinalTutorialModalOpen(false);
    postShouldNeverShowTutorial();
  };

  useEffect(() => {
    Promise.all([
      getCollections().then((response) => {
        const dict = [{ value: "all", label: "Todos" }];
        response.data.map((collection) => {
          return dict.push({
            value: collection.value,
            label: collection.label,
          });
        });

        dict.sort((a, b) => a.value - b.value);
        setZonesList(dict);
      }),

      Promise.all([getZoneConfigs(), getZones()])
        .then(([configsResponse, zonesResponse]) => {
          const activeConfig = configsResponse.data.find(
            (config) => config.status === true
          );

          if (activeConfig) {
            const activeConfigId = activeConfig.id;

            const activeZones = zonesResponse.data.filter(
              (zone) => zone.configuration === activeConfigId
            );

            const dict = [];

            activeZones.forEach((zone) => {
              dict.push({ value: zone.value, label: zone.label });
            });

            dict.sort((a, b) => a.value - b.value);

            setZonesList(dict);
          } else {
            console.error("No congiguration found");
          }
        })
        .catch((error) => console.error(error)),
    ]).catch((error) => console.error(error));
  }, []);

  const tutorialColletctionRef = {
    selectMode: useRef(null),
    selectZone: useRef(null),
    includeCollecitionButton: useRef(null),
  };

  const steps = [
    {
      title: "13º passo: Clique no seletor para abrir o menu",
      description: "Clique no seletor e selecione 'Gerenciar zonas e coleções'",
      nextButtonProps: {
        children: "OK",
        onClick: () => {
          setSelectedOption("add_cameras");
          setIsEighthPhaseTutorialOpen(true);
        },
      },
      target: () => tutorialColletctionRef.selectMode.current,
    },
    {
      title: "14º passo: Clique no seletor para configurar as zonas",
      description: "Escolhe em qual zona você deseja cadastrar as coleções",
      nextButtonProps: {
        children: "OK",
        onClick: () => {
          setIsEighthPhaseTutorialOpen(false);
          setIsNinthPhaseTutorialOpen(true);
        },
      },
      prevButtonProps: {
        children: "Anterior",
      },
      target: () => tutorialColletctionRef.selectZone.current,
    },
    {
      title: "15º passo: Clique no botão para incluir uma nova coleção",
      description:
        "Clique no botão e cadastre o nome e a data de inicio de relação entre a zona e a coleção",
      nextButtonProps: {
        children: "OK",
      },
      target: () => tutorialColletctionRef.includeCollecitionButton.current,
    },
  ];

  return (
    <>
      {contextHolder}
      <ConfigProvider
        theme={{
          components: {
            Card: {
              headerBg: "#d0d6e2",
            },
          },
        }}>
        <Flex
          style={{ width: "100%", margin: "0 auto" }}
          justify="center"
          align="center"
          vertical
          gap={20}>
          <Card
            title="Registre eventos e edite as suas coleções!"
            style={{
              width: "90%",
              backgroundColor: "#d0d6e2",
            }}>
            <Row>
              <Col span={24}>
                <Text style={{ fontSize: 16 }} strong>
                  O que você deseja fazer?
                </Text>
              </Col>
            </Row>
          </Card>

          <Row>
            <Col span={16}>
              <div
                style={{
                  display: "flex",
                  gap: 18,
                  flexWrap: "nowrap",
                  justifyContent: "flex-start",
                  width: "100%",
                }}>
                {cardOptions.map(({ value, label, icon }) => (
                  <Button
                    key={value}
                    type={selectedOption === value ? "primary" : "default"}
                    onClick={() => {
                      setSelectedOption(value);
                      cleanUsesState();
                      setIsSeventhPhaseTutorialOpen(false);
                      setIsEighthPhaseTutorialOpen(true);
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "8px 12px",
                      textAlign: "center",
                      height: 70,
                    }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>

                    <div
                      style={{
                        fontSize: 14,
                        whiteSpace: "nowrap",
                      }}>
                      {label}
                    </div>
                  </Button>
                ))}
              </div>
            </Col>
          </Row>

          {selectedOption === "new_event" ? (
            <Card title="Registre um novo evento" style={{ width: "85%" }}>
              <Row
                gutter={[16, 16]}
                style={{ display: "flex", alignItems: "center" }}>
                <Col span={4}>
                  <Text strong>Zona afetada: </Text>
                </Col>
                <Col span={20}>
                  <Select
                    mode="multiple"
                    options={zonesList}
                    style={{ width: "100%" }}
                    onChange={(e) => setZoneAffected(e)}
                    selectedOption={zoneAffected}></Select>
                </Col>
                <Col span={24} align="center" style={{ paddingTop: 20 }}>
                  <Text strong>Descrição do evento </Text>
                </Col>
                <Col span={4}>
                  <Text strong>Título: </Text>
                </Col>
                <Col span={20}>
                  <Input
                    value={newEventTitle}
                    onChange={(e) => setNewEventTitle(e.target.value)}></Input>
                </Col>
                <Col span={4}>
                  <Text strong>Comentário: </Text>
                </Col>
                <Col span={20}>
                  <TextArea
                    rows={4}
                    placeholder="Máximo de 200 caracteres"
                    maxLength={200}
                    value={newEventComment}
                    onChange={(e) =>
                      setNewEventComment(e.target.value)
                    }></TextArea>
                </Col>
                <Col span={4}>
                  <Text strong>Data e hora do evento: </Text>
                </Col>
                <Col span={20}>
                  <DatePicker
                    showTime
                    value={selectedDate ? dayjs(selectedDate) : datePickerNow}
                    onChange={(date, dateString) => setSelectedDate(dateString)}
                  />
                  <Button
                    onClick={() => {
                      setDatePickerNow(dayjs(new Date().toISOString()));
                      setSelectedDate(new Date().toISOString());
                    }}
                    style={{ marginLeft: 10 }}>
                    Agora
                  </Button>
                </Col>
                <Col span={24} align="center">
                  <Button type="primary" onClick={createEvent}>
                    Adicionar
                  </Button>
                </Col>
              </Row>
            </Card>
          ) : selectedOption === "edit_zone_collection" ? (
            <Card
              ref={tutorialColletctionRef.selectZone}
              title="Mude a coleção de uma zona"
              style={{ width: "85%" }}>
              <Row
                gutter={[16, 32]}
                style={{ display: "flex", alignItems: "center" }}>
                <Col span={4}>
                  <Text strong>Selecione a zona:</Text>
                </Col>
                <Col span={20}>
                  <Select
                    options={zonesList}
                    style={{ width: "35%" }}
                    onChange={(e) => {
                      setZoneId(e);
                      setIsEighthPhaseTutorialOpen(false);
                      setIsNinthPhaseTutorialOpen(true);
                    }}
                    value={zoneId}
                  />
                </Col>
              </Row>
              {/* Renderizar card e botões somente se zoneId estiver preenchido */}
              {zoneId && (
                <>
                  <Row
                    gutter={[16, 32]}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      alignItems: "center",
                    }}>
                    <Col span={16}>
                      <Text strong>
                        Gerencie as coleções atuais da zona selecionada:
                      </Text>
                    </Col>
                    <Col span={24}>
                      <Table
                        columns={[
                          {
                            title: "Coleção",
                            dataIndex: "collection",
                            width: "200px",
                            key: "collection",
                          },
                          {
                            title: "Início da relação",
                            dataIndex: "union_start_datetime",
                            width: "200px",
                            key: "union_start_datetime",
                            render: (text, record) => (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}>
                                <span style={{ marginRight: "20px" }}>
                                  {text}
                                </span>
                                <Button
                                  style={{ borderColor: "blue" }}
                                  icon={
                                    <CalendarOutlined
                                      style={{ color: "blue" }}
                                    />
                                  }
                                  onClick={() => {
                                    setShowStartDateModal(true);
                                    setCollectionToEditStartDate(record);
                                    setSelectedEditStartDate(
                                      record.union_start_datetime
                                        ? dayjs(record.union_start_datetime)
                                        : null
                                    );
                                  }}
                                />
                              </div>
                            ),
                          },
                          {
                            title: "Substituir coleção",
                            dataIndex: "replace",
                            key: "replace",
                            width: "200px",
                            render: (text, record) => (
                              <Button
                                style={{ borderColor: "blue" }}
                                icon={
                                  <RetweetOutlined style={{ color: "blue" }} />
                                }
                                onClick={() => {
                                  setShowReplaceModal(true);
                                  setCollectionToReplace(record);
                                  setSelectedNewCollectionForReplace(null);
                                }}></Button>
                            ),
                          },
                          {
                            title: "Encerrar relação",
                            dataIndex: "remove",
                            width: "200px",
                            key: "remove",
                            render: (text, record) => (
                              <Button
                                danger
                                icon={<CloseOutlined />}
                                onClick={() => {
                                  setCollectionToRemove(record);
                                  setSelectedRemoveEndDate(null);
                                  setShowRemoveModal(true);
                                }}></Button>
                            ),
                          },
                        ]}
                        dataSource={zoneCollection}
                        pagination={false}
                        locale={{
                          emptyText: (
                            <div style={{ textAlign: "center" }}>
                              <CloseOutlined
                                style={{
                                  fontSize: "24px",
                                  marginTop: 10,
                                }}></CloseOutlined>
                              <p>Zona não possui nenhuma coleção cadastrada</p>
                            </div>
                          ),
                        }}
                      />
                    </Col>
                    <Col span={4}>
                      <Button
                        ref={tutorialColletctionRef.includeCollecitionButton}
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setIsAddingCollection(!isAddingCollection);
                          setIsNinthPhaseTutorialOpen(false);
                        }}>
                        Incluir uma nova coleção
                      </Button>
                    </Col>
                  </Row>
                  {isAddingCollection && (
                    <Row
                      gutter={[16, 16]}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: 20,
                      }}>
                      <Col span={24}>
                        <Text strong>Nome da coleção</Text>
                      </Col>
                      <Col span={24}>
                        <Select
                          showSearch
                          placeholder="Selecione uma coleção"
                          value={selectedCollection?.value || null}
                          onChange={(value) => {
                            const collection = collections.find(
                              (col) => col.value === value
                            );
                            setSelectedCollection(collection);
                          }}
                          options={collections.map((collection) => ({
                            value: collection.value,
                            label: collection.label,
                          }))}
                          style={{ width: "18%" }}
                          optionFilterProp="label"
                        />
                      </Col>

                      <Col span={24} style={{ marginTop: 10 }}>
                        <Text strong>Data & Hora de início da relação</Text>
                      </Col>
                      <Col span={24}>
                        <DatePicker
                          showTime
                          style={{ width: "18%", marginRight: "10px" }}
                          value={
                            newCollectionStartDate
                              ? dayjs(newCollectionStartDate)
                              : null
                          }
                          onChange={(date) => {
                            setNewCollectionStartDate(date);
                          }}></DatePicker>
                        <Button
                          onClick={() => {
                            const now = dayjs(new Date().toISOString());
                            setNewCollectionStartDate(now);
                          }}>
                          Agora
                        </Button>
                      </Col>

                      <Col span={24} align="left" style={{ marginTop: 10 }}>
                        <Button
                          type="primary"
                          onClick={registerCollectionAndZone}
                          disabled={
                            !selectedCollection || !newCollectionStartDate
                          }>
                          Adicionar
                        </Button>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Card>
          ) : (
            <></>
          )}

          {/*Modal para substituir uma coleção*/}

          <Modal
            open={showReplaceModal}
            onCancel={() => {
              setShowReplaceModal(false);
              setSelectedEndDate(null);
            }}
            title="Substituir Coleção"
            footer={null}
            styles={{
              body: {
                height: "25vh",
              },
            }}>
            <p>
              Você está prestes a substituir a relação entre{" "}
              {zonesList.find((zone) => zone.value === zoneId)?.label || zoneId}{" "}
              e {collectionToReplace?.collection}
            </p>

            <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
              <Col span={24}>
                <Text strong>Data/Hora de encerramento:</Text>
              </Col>
              <Col span={24}>
                <DatePicker
                  showTime
                  style={{ width: "70%", marginRight: "10px" }}
                  value={selectedEndDate ? dayjs(selectedEndDate) : null}
                  onChange={(date) => {
                    setSelectedEndDate(date);
                  }}
                />
                <Button
                  onClick={() => {
                    const now = dayjs(new Date().toISOString());
                    setSelectedEndDate(now);
                  }}>
                  Agora
                </Button>
              </Col>
            </Row>

            <Select
              showSearch
              placeholder="Selecione uma nova coleção"
              value={selectedNewCollectionForReplace?.value || null}
              onChange={(value) => {
                const newCol = collections.find((col) => col.value === value);
                setSelectedNewCollectionForReplace(newCol);
              }}
              options={collections.map((collection) => ({
                value: collection.value,
                label: collection.label,
              }))}
              style={{ width: "100%", marginBottom: "25px" }}
              optionFilterProp="label"
            />

            <div style={{ textAlign: "right" }}>
              <Button
                onClick={() => {
                  setShowReplaceModal(false);
                  setSelectedEndDate(null);
                }}
                style={{ marginRight: 10 }}>
                Cancelar
              </Button>
              <Button
                type="primary"
                onClick={() =>
                  updateCollection(
                    collectionToReplace,
                    selectedNewCollectionForReplace
                  )
                }
                disabled={!selectedNewCollectionForReplace}>
                Confirmar
              </Button>
            </div>
          </Modal>

          {/*Modal para remover uma coleção*/}

          <Modal
            open={showRemoveModal}
            onCancel={() => {
              setShowRemoveModal(false);
              setSelectedRemoveEndDate(null);
            }}
            title="Remover Coleção"
            footer={null}
            styles={{
              body: {
                height: "20vh",
              },
            }}>
            <p>
              Você está prestes a encerrar a relação entre{" "}
              {zonesList.find((zone) => zone.value === zoneId)?.label || zoneId}{" "}
              e {collectionToRemove?.collection}
            </p>

            <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
              <Col span={24}>
                <Text strong>Data/Hora de encerramento:</Text>
              </Col>
              <Col span={24}>
                <DatePicker
                  showTime
                  style={{ width: "70%", marginRight: "10px" }}
                  value={
                    selectedRemoveEndDate ? dayjs(selectedRemoveEndDate) : null
                  }
                  onChange={(date) => {
                    setSelectedRemoveEndDate(date);
                  }}
                />
                <Button
                  onClick={() => {
                    const now = dayjs(new Date().toISOString());
                    setSelectedRemoveEndDate(now);
                  }}>
                  Agora
                </Button>
              </Col>
            </Row>

            <div style={{ textAlign: "right", marginTop: "32px" }}>
              <Button
                onClick={() => {
                  setShowRemoveModal(false);
                  setSelectedRemoveEndDate(null);
                }}
                style={{ marginRight: 10 }}>
                Cancelar
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  removeCollection(
                    collectionToRemove,
                    selectedRemoveEndDate
                  ).then(() => {
                    setShowRemoveModal(false);
                    setSelectedRemoveEndDate(null);
                  });
                }}
                disabled={!selectedRemoveEndDate}>
                Confirmar
              </Button>
            </div>
          </Modal>

          {/*Modal para editar data de início*/}
          <Modal
            open={showStartDateModal}
            onCancel={() => {
              setShowStartDateModal(false);
              setSelectedEditStartDate(null);
            }}
            title="Editar Data de Início"
            footer={null}>
            <p>
              Você está prestes a editar a data de início da relação entre{" "}
              {zonesList.find((zone) => zone.value === zoneId)?.label || zoneId}{" "}
              e {collectionToEditStartDate?.collection}
            </p>

            <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
              <Col span={24}>
                <Text strong>Data/Hora de início:</Text>
              </Col>
              <Col span={24}>
                <DatePicker
                  showTime
                  style={{ width: "70%", marginRight: "10px" }}
                  value={
                    selectedEditStartDate ? dayjs(selectedEditStartDate) : null
                  }
                  onChange={(date) => {
                    setSelectedEditStartDate(date);
                  }}
                />
                <Button
                  onClick={() => {
                    const now = dayjs(new Date().toISOString());
                    setSelectedEditStartDate(now);
                  }}>
                  Agora
                </Button>
              </Col>
            </Row>

            <div style={{ textAlign: "right", marginTop: "32px" }}>
              <Button
                onClick={() => {
                  setShowStartDateModal(false);
                  setSelectedEditStartDate(null);
                }}
                style={{ marginRight: 10 }}>
                Cancelar
              </Button>
              <Button
                type="primary"
                disabled={!selectedEditStartDate}
                onClick={updateStartDate}>
                Confirmar
              </Button>
            </div>
          </Modal>
        </Flex>
        <Tour
          open={shouldShowTutorialBool && isSeventhPhaseTutorialOpen}
          steps={steps.slice(0, 1)}
          onClose={() => {
            setIsSeventhPhaseTutorialOpen(false);
          }}
          gap={{
            offset: [20, 10],
            radius: 10,
          }}
        />
        <Tour
          open={
            shouldShowTutorialBool &&
            isEighthPhaseTutorialOpen &&
            selectedOption === "edit_zone_collection"
          }
          steps={steps.slice(1, 2)}
          onClose={() => {
            setIsEighthPhaseTutorialOpen(false);
          }}
          gap={{
            offset: [20, 10],
            radius: 10,
          }}
        />
        <Tour
          open={shouldShowTutorialBool && isNinthPhaseTutorialOpen}
          steps={steps.slice(2, 3)}
          onClose={() => {
            setIsNinthPhaseTutorialOpen(false);
          }}
          gap={{
            offset: [20, 10],
            radius: 10,
          }}
        />
        <Modal
          title="Tutorial Concluído!"
          open={isFinalTutorialModalOpen && shouldShowTutorialBool}
          onOk={handleFinalTutorial}
          okText="Finalizar Tutorial"
          cancelText="Reiniciar tutorial"
          onCancel={() => navigate("/")}>
          <p style={{ fontSize: 40, textAlign: "center", margin: "1rem" }}>
            &#9989;
          </p>
          <p>
            Parabéns, você concluiu todo o tutorial! Agora, pode utilizar a
            plataforma de forma completa. Explore dados de entrada da loja,
            informações de faturamento e muito mais. Além disso, você também
            pode cadastrar eventos sempre que houver alterações nas coleções.
            Sinta-se à vontade para explorar todas as funcionalidades da
            plataforma!
          </p>
        </Modal>
      </ConfigProvider>
    </>
  );
}
